import { useEffect, useState, useRef, type CSSProperties } from "react";
import { onSlowNetwork, onOfflineStatusChange } from "../utils/networkMonitor";
import { FaExclamationTriangle } from "../utils/icons";
import offlineIcon from "../assets/icons8-offline-50.png";
import onlineIcon from "../assets/icons8-online-50.png";

// Both PNGs are black-outline icons on a transparent background — inverted to
// white via CSS filter so they read clearly against the banner's solid color,
// same as the white text next to them.
const iconImgStyle: CSSProperties = {
  width: 18,
  height: 18,
  flexShrink: 0,
  filter: "brightness(0) invert(1)",
};

const VISIBLE_DURATION_MS = 6000;
const BACK_ONLINE_DURATION_MS = 3000;
const FADE_MS = 300;

export default function NetworkStatusBanner() {
  // Fully offline is a distinct, more urgent state than "slow" — it's shown
  // for as long as the browser reports no connection at all (not auto-hidden
  // on a timer, since the problem is ongoing until connectivity returns),
  // then briefly confirms when the connection comes back.
  const [isOffline, setIsOffline] = useState(false);
  const [showBackOnline, setShowBackOnline] = useState(false);
  const wasOfflineRef = useRef(false);
  const backOnlineTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // `mounted` actually removes the element from the DOM once it's done being
  // shown — `visible` just drives the fade so it doesn't vanish abruptly.
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const unmountTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const unsubscribeOffline = onOfflineStatusChange((offline) => {
      setIsOffline(offline);
      if (offline) {
        wasOfflineRef.current = true;
        setShowBackOnline(false);
        if (backOnlineTimerRef.current) clearTimeout(backOnlineTimerRef.current);
      } else if (wasOfflineRef.current) {
        wasOfflineRef.current = false;
        setShowBackOnline(true);
        backOnlineTimerRef.current = setTimeout(() => setShowBackOnline(false), BACK_ONLINE_DURATION_MS);
      }
    });
    return () => {
      unsubscribeOffline();
      if (backOnlineTimerRef.current) clearTimeout(backOnlineTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const unsubscribe = onSlowNetwork(() => {
      // Being offline already says everything the slow-network toast would —
      // skip it rather than stacking a second banner on top.
      if (wasOfflineRef.current || !navigator.onLine) return;

      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      if (unmountTimerRef.current) clearTimeout(unmountTimerRef.current);

      setMounted(true);
      requestAnimationFrame(() => setVisible(true));

      hideTimerRef.current = setTimeout(() => {
        setVisible(false);
        unmountTimerRef.current = setTimeout(() => setMounted(false), FADE_MS);
      }, VISIBLE_DURATION_MS);
    });
    return () => {
      unsubscribe();
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      if (unmountTimerRef.current) clearTimeout(unmountTimerRef.current);
    };
  }, []);

  if (isOffline) {
    return (
      <div role="alert" aria-live="assertive" className="network-status-banner" style={{ background: "#b91c1c" }}>
        <img src={offlineIcon} alt="" style={iconImgStyle} />
        You're offline — check your internet connection.
      </div>
    );
  }

  if (showBackOnline) {
    return (
      <div role="status" aria-live="polite" className="network-status-banner" style={{ background: "#15803d" }}>
        <img src={onlineIcon} alt="" style={iconImgStyle} />
        Back online.
      </div>
    );
  }

  if (!mounted) return null;

  const fadeStyle: CSSProperties = {
    background: "#92400e",
    opacity: visible ? 1 : 0,
    transition: `opacity ${FADE_MS}ms ease`,
  };

  return (
    <div role="status" aria-live="polite" className="network-status-banner" style={fadeStyle}>
      <FaExclamationTriangle className="network-status-banner__icon" />
      Your network connection seems slow — some things may take longer to load.
    </div>
  );
}
